import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ShoppingBag,
  Clock,
  MapPin,
  Phone,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Bike,
  Car,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Volume2,
  VolumeX,
  ChefHat,
  PackageCheck,
  CheckCheck,
  Sparkles,
  ExternalLink,
  PlusCircle,
  Loader2,
  SlidersHorizontal,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/src/components/ui/card";
import { Button } from "@/src/components/ui/button";
import { Switch } from "@/src/components/ui/switch";
import { useAuth } from "../../contexts/AuthContext";
import { getSupabaseClient } from "../../lib/supabase";
import { toast } from "sonner";

interface OnlineOrderRecord {
  id: string;
  customer_name: string;
  customer_phone: string;
  delivery_address: string;
  total: number;
  payment_status: string;
  created_at: string;
  token_number?: number;
  cancellation_reason?: string;
  kot?: {
    id: string;
    status: string;
    notes?: string;
  };
  items: {
    name: string;
    quantity: number;
    price?: number;
  }[];
}

function formatTimeAgo(dateString: string): string {
  if (!dateString) return "Just now";
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffSec = Math.max(0, Math.floor(diffMs / 1000));
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? "s" : ""} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
}

export const HospitalityOnlineOrders: React.FC = () => {
  const { currentTenantId, activeBranchId } = useAuth();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [orders, setOrders] = useState<OnlineOrderRecord[]>([]);
  const [autoAccept, setAutoAccept] = useState(false);
  const [savingAutoAccept, setSavingAutoAccept] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Collapsible section toggles
  const [inKitchenOpen, setInKitchenOpen] = useState(true);
  const [readyOpen, setReadyOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Action states
  const [approvingKotId, setApprovingKotId] = useState<string | null>(null);

  // Reject Modal State
  const [rejectingOrder, setRejectingOrder] = useState<OnlineOrderRecord | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [submittingReject, setSubmittingReject] = useState(false);

  // Delivery Modal State
  const [dispatchOrder, setDispatchOrder] = useState<OnlineOrderRecord | null>(null);
  const [vehicleType, setVehicleType] = useState<"bike" | "car">("bike");
  const [dispatching, setDispatching] = useState(false);

  // Test Order Simulator State
  const [simulatingOrder, setSimulatingOrder] = useState(false);

  // Track previous pending count for audio alert
  const prevPendingCountRef = useRef<number>(0);

  // Web Audio chime for new pending orders
  const playAlertChime = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.12); // A5
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    } catch {
      // Audio playback restrictions handled gracefully
    }
  }, [soundEnabled]);

  // 0. Fetch Auto-Accept setting
  const fetchDeliverySettings = useCallback(async () => {
    if (!activeBranchId) return;
    const supabase = getSupabaseClient();
    if (!supabase) return;

    try {
      const { data, error } = await supabase
        .from("hospitality_delivery_settings")
        .select("auto_accept_online_orders")
        .eq("branch_id", activeBranchId)
        .maybeSingle();

      if (!error && data) {
        setAutoAccept(Boolean(data.auto_accept_online_orders));
      } else {
        // row na mile to switch OFF dikhao — yehi system default hai
        setAutoAccept(false);
      }
    } catch (err) {
      console.warn("Could not load hospitality_delivery_settings:", err);
      setAutoAccept(false);
    }
  }, [activeBranchId]);

  // Toggle Auto-Accept
  const handleToggleAutoAccept = async (newValue: boolean) => {
    if (!activeBranchId || !currentTenantId) {
      toast.error("Branch or Tenant context not available");
      return;
    }
    const supabase = getSupabaseClient();
    if (!supabase) return;

    setSavingAutoAccept(true);
    try {
      const { error } = await supabase
        .from("hospitality_delivery_settings")
        .upsert(
          {
            branch_id: activeBranchId,
            tenant_id: currentTenantId,
            auto_accept_online_orders: newValue,
          },
          { onConflict: "branch_id" }
        );

      if (error) {
        toast.error(`Setting update failed: ${error.message}`);
      } else {
        setAutoAccept(newValue);
        if (newValue) {
          toast.success("Auto-Accept ON: Incoming online orders will go directly to kitchen.");
        } else {
          toast.info("Auto-Accept OFF: Incoming online orders will require manual Approval.");
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update auto-accept setting");
    } finally {
      setSavingAutoAccept(false);
    }
  };

  // 1. Fetch Orders with robust fallback
  const fetchOnlineOrders = useCallback(
    async (isManualRefresh = false) => {
      if (!currentTenantId || !activeBranchId) return;
      const supabase = getSupabaseClient();
      if (!supabase) return;

      if (isManualRefresh) setRefreshing(true);

      try {
        // First try primary join query as specified in prompt
        const { data, error } = await supabase
          .from("orders")
          .select(`
            id, customer_name, customer_phone, delivery_address, total, payment_status, created_at, token_number, cancellation_reason,
            kitchen_order_tickets!inner(id, status, notes),
            order_items(quantity, price, restaurant_menu_items(item_name))
          `)
          .eq("tenant_id", currentTenantId)
          .eq("branch_id", activeBranchId)
          .eq("order_type", "online")
          .order("created_at", { ascending: false });

        if (!error && data) {
          const mapped: OnlineOrderRecord[] = data.map((o: any) => {
            const kots = Array.isArray(o.kitchen_order_tickets)
              ? o.kitchen_order_tickets
              : o.kitchen_order_tickets
              ? [o.kitchen_order_tickets]
              : [];
            const primaryKot = kots[0] || null;

            const items = (o.order_items || []).map((oi: any) => ({
              name: oi.restaurant_menu_items?.item_name || "Item",
              quantity: oi.quantity || 1,
              price: oi.price || 0,
            }));

            return {
              id: o.id,
              customer_name: o.customer_name || "Online Guest",
              customer_phone: o.customer_phone || "No phone",
              delivery_address: o.delivery_address || "No address provided",
              total: o.total || 0,
              payment_status: o.payment_status || "unpaid",
              created_at: o.created_at,
              token_number: o.token_number,
              cancellation_reason: o.cancellation_reason,
              kot: primaryKot
                ? {
                    id: primaryKot.id,
                    status: primaryKot.status,
                    notes: primaryKot.notes,
                  }
                : undefined,
              items,
            };
          });

          setOrders(mapped);
          setLastUpdated(new Date());

          // Check if new pending orders arrived to sound alert
          const pendingCount = mapped.filter((o) => o.kot?.status === "pending_approval").length;
          if (pendingCount > prevPendingCountRef.current) {
            playAlertChime();
          }
          prevPendingCountRef.current = pendingCount;
          return;
        }

        // Fallback: If client nested join had issues, fetch separately and combine in JS
        console.warn("Nested query fallback executing:", error?.message);
        const { data: ords } = await supabase
          .from("orders")
          .select("*")
          .eq("tenant_id", currentTenantId)
          .eq("branch_id", activeBranchId)
          .eq("order_type", "online")
          .order("created_at", { ascending: false })
          .limit(100);

        if (!ords || ords.length === 0) {
          setOrders([]);
          setLastUpdated(new Date());
          return;
        }

        const ordIds = ords.map((o: any) => o.id);
        const [{ data: kotsData }, { data: itemsData }] = await Promise.all([
          supabase
            .from("kitchen_order_tickets")
            .select("id, order_id, status, notes")
            .in("order_id", ordIds),
          supabase
            .from("order_items")
            .select("order_id, quantity, price, restaurant_menu_items(item_name)")
            .in("order_id", ordIds),
        ]);

        const mappedFallback: OnlineOrderRecord[] = ords.map((o: any) => {
          const matchingKot = (kotsData || []).find((k: any) => k.order_id === o.id);
          const matchingItems = (itemsData || [])
            .filter((i: any) => i.order_id === o.id)
            .map((i: any) => ({
              name: i.restaurant_menu_items?.item_name || "Item",
              quantity: i.quantity || 1,
              price: i.price || 0,
            }));

          return {
            id: o.id,
            customer_name: o.customer_name || "Online Guest",
            customer_phone: o.customer_phone || "No phone",
            delivery_address: o.delivery_address || "No address provided",
            total: o.total || 0,
            payment_status: o.payment_status || "unpaid",
            created_at: o.created_at,
            token_number: o.token_number,
            cancellation_reason: o.cancellation_reason,
            kot: matchingKot
              ? {
                  id: matchingKot.id,
                  status: matchingKot.status,
                  notes: matchingKot.notes,
                }
              : undefined,
            items: matchingItems,
          };
        });

        setOrders(mappedFallback);
        setLastUpdated(new Date());

        const pendingCount = mappedFallback.filter((o) => o.kot?.status === "pending_approval").length;
        if (pendingCount > prevPendingCountRef.current) {
          playAlertChime();
        }
        prevPendingCountRef.current = pendingCount;
      } catch (err: any) {
        console.error("Error fetching online orders:", err);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [currentTenantId, activeBranchId, playAlertChime]
  );

  // Initial load and Realtime setup
  useEffect(() => {
    fetchDeliverySettings();
    fetchOnlineOrders();

    const supabase = getSupabaseClient();
    if (!supabase) return;

    // Supabase realtime subscription
    const channel = supabase
      .channel("online_orders_kds_channel")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders" },
        () => fetchOnlineOrders()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "kitchen_order_tickets" },
        () => fetchOnlineOrders()
      )
      .subscribe();

    // 30-second polling fallback
    const intervalId = setInterval(() => {
      fetchOnlineOrders();
    }, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(intervalId);
    };
  }, [fetchDeliverySettings, fetchOnlineOrders]);

  // 2. Action: Approve KOT
  const handleApprove = async (order: OnlineOrderRecord) => {
    const kotId = order.kot?.id;
    if (!kotId) {
      toast.error("No KOT associated with this order");
      return;
    }

    const supabase = getSupabaseClient();
    if (!supabase) return;

    setApprovingKotId(kotId);
    try {
      const { error } = await supabase.rpc("approve_kot", { p_kot_id: kotId });
      if (error) {
        toast.error(`Approval failed: ${error.message}`);
      } else {
        toast.success(`Order #${order.token_number || order.id.slice(0, 6)} approved! Sent to Kitchen.`);
        // Optimistically update in memory while realtime confirms
        setOrders((prev) =>
          prev.map((o) =>
            o.id === order.id && o.kot ? { ...o, kot: { ...o.kot, status: "new" } } : o
          )
        );
        fetchOnlineOrders();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to approve order");
    } finally {
      setApprovingKotId(null);
    }
  };

  // 2. Action: Reject KOT with reason
  const handleConfirmReject = async () => {
    if (!rejectingOrder || !rejectingOrder.kot?.id) return;
    const kotId = rejectingOrder.kot.id;
    const finalReason = rejectReason.trim() || "Rejected by manager";

    const supabase = getSupabaseClient();
    if (!supabase) return;

    setSubmittingReject(true);
    try {
      const { error } = await supabase.rpc("reject_kot", {
        p_kot_id: kotId,
        p_reason: finalReason,
      });

      if (error) {
        toast.error(`Rejection failed: ${error.message}`);
      } else {
        toast.success(`Order #${rejectingOrder.token_number || rejectingOrder.id.slice(0, 6)} rejected.`);
        setRejectingOrder(null);
        setRejectReason("");
        fetchOnlineOrders();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to reject order");
    } finally {
      setSubmittingReject(false);
    }
  };

  // 3. Action: Send for Delivery
  const handleConfirmDispatch = async () => {
    if (!dispatchOrder) return;
    const orderId = dispatchOrder.id;

    const supabase = getSupabaseClient();
    if (!supabase) return;

    setDispatching(true);
    try {
      const { data: jobId, error } = await supabase.rpc("create_delivery_job", {
        p_order_id: orderId,
        p_vehicle_type: vehicleType,
      });

      if (error) {
        toast.error(`Delivery dispatch failed: ${error.message}`);
      } else {
        toast.success(`Delivery job created with ${vehicleType.toUpperCase()}! Rider being dispatched.`);
        setDispatchOrder(null);
        fetchOnlineOrders();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to dispatch delivery job");
    } finally {
      setDispatching(false);
    }
  };

  // Quick Test Simulator: Places a sample online order
  const handleSimulateTestOrder = async () => {
    if (!currentTenantId || !activeBranchId) {
      toast.error("Tenant or Branch not found");
      return;
    }
    const supabase = getSupabaseClient();
    if (!supabase) return;

    setSimulatingOrder(true);
    try {
      // Find a menu item from this branch/tenant
      const { data: menuItems } = await supabase
        .from("restaurant_menu_items")
        .select("id, item_name, price")
        .eq("tenant_id", currentTenantId)
        .limit(2);

      const itemsToOrder =
        menuItems && menuItems.length > 0
          ? menuItems.map((m: any) => ({ menu_item_id: m.id, quantity: 1 }))
          : [{ menu_item_id: "22222222-2222-2222-2222-000000000001", quantity: 1 }];

      const testNames = ["Aman Sharma", "Priya Verma", "Rahul Mehta", "Neha Kapoor", "Vikram Rathore"];
      const randomName = testNames[Math.floor(Math.random() * testNames.length)];
      const randomPhone = `98${Math.floor(10000000 + Math.random() * 90000000)}`;
      const randomAddress = `Flat ${Math.floor(100 + Math.random() * 900)}, Green Park Residency, Sector ${Math.floor(1 + Math.random() * 25)}`;

      // Use current auth user if available
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();

      const { data, error } = await supabase.rpc("create_food_order", {
        p_tenant_id: currentTenantId,
        p_branch_id: activeBranchId,
        p_user_id: currentUser?.id || null,
        p_customer_name: randomName,
        p_customer_phone: randomPhone,
        p_delivery_address: randomAddress,
        p_payment_method: "cod",
        p_items: itemsToOrder,
        p_order_type: "online",
      });

      if (error) {
        toast.error(`Test order failed: ${error.message}`);
      } else {
        const orderStatus = (data as any)?.status || "pending_approval";
        toast.success(
          `Test Online Order placed! Status: ${orderStatus}. ${
            orderStatus === "pending_approval" ? "Check 'Pending Approval' below!" : "Auto-accepted to 'In Kitchen'!"
          }`
        );
        fetchOnlineOrders();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to create test order");
    } finally {
      setSimulatingOrder(false);
    }
  };

  // Group orders by section according to KOT status
  const pendingOrders = orders.filter((o) => o.kot?.status === "pending_approval");
  const inKitchenOrders = orders.filter(
    (o) => o.kot?.status === "new" || o.kot?.status === "preparing"
  );
  const readyOrders = orders.filter((o) => o.kot?.status === "ready");
  const historyOrders = orders.filter(
    (o) => o.kot?.status === "served" || o.kot?.status === "rejected"
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* 0. HEADER & AUTO-ACCEPT TOGGLE */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 text-orange-600 dark:text-orange-400 flex items-center justify-center font-bold">
                <ShoppingBag size={22} />
              </div>
              <div>
                <h1 className="text-2xl font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  Online Orders
                  {pendingOrders.length > 0 && (
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                    </span>
                  )}
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Online delivery & pickup orders approval desk. Approved orders are sent to Kitchen (KDS).
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Sound alert toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSoundEnabled((v) => !v)}
              className="h-9 px-3 text-xs gap-1.5 border-slate-200 dark:border-slate-800"
              title={soundEnabled ? "Mute audio alerts" : "Enable sound alerts"}
            >
              {soundEnabled ? (
                <>
                  <Volume2 size={15} className="text-emerald-600 dark:text-emerald-400" />
                  <span className="text-slate-700 dark:text-slate-300">Sound ON</span>
                </>
              ) : (
                <>
                  <VolumeX size={15} className="text-slate-400" />
                  <span className="text-slate-400">Sound Muted</span>
                </>
              )}
            </Button>

            {/* Manual refresh button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchOnlineOrders(true)}
              disabled={refreshing}
              className="h-9 px-3 text-xs gap-1.5 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300"
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin text-primary" : ""} />
              <span>Refresh</span>
            </Button>

            {/* Test online order generator button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSimulateTestOrder}
              disabled={simulatingOrder}
              className="h-9 px-3 text-xs gap-1.5 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/60"
            >
              {simulatingOrder ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <PlusCircle size={14} />
              )}
              <span>+ Test Order</span>
            </Button>
          </div>
        </div>

        {/* 0. AUTO-ACCEPT TOGGLE BAR */}
        <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50 dark:bg-slate-950/50 p-4 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 mt-0.5">
              <SlidersHorizontal size={18} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm text-slate-900 dark:text-slate-100">
                  Auto-Accept Online Orders
                </span>
                <span
                  className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                    autoAccept
                      ? "bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800"
                      : "bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                  }`}
                >
                  {autoAccept ? "Enabled (Auto)" : "Disabled (Manual Approval)"}
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 max-w-2xl">
                {autoAccept
                  ? "Naye online orders automatically confirm hokar seedha kitchen KDS mein chale jayenge."
                  : "Naye orders 'Pending Approval' mein aayenge jab tak staff unhe manually Approve na karein (Default)."}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 self-end sm:self-center">
            {savingAutoAccept && <Loader2 size={16} className="animate-spin text-slate-400" />}
            <Switch
              id="auto-accept-switch"
              checked={autoAccept}
              onCheckedChange={handleToggleAutoAccept}
              disabled={savingAutoAccept}
            />
          </div>
        </div>
      </div>

      {/* 2. MAIN PROMINENT SECTION — "Pending Approval" */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold">
              <AlertCircle size={18} />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                Pending Approval
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                    pendingOrders.length > 0
                      ? "bg-amber-500 text-white animate-pulse"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                  }`}
                >
                  {pendingOrders.length}
                </span>
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Action required: review and approve incoming orders to push to kitchen
              </p>
            </div>
          </div>

          {pendingOrders.length > 0 && (
            <div className="flex items-center gap-1.5 text-xs font-bold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 border border-amber-300 dark:border-amber-800 px-3 py-1 rounded-full">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping"></span>
              <span>{pendingOrders.length} Order(s) waiting for approval</span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 text-center">
            <Loader2 className="animate-spin mx-auto text-primary mb-2" size={28} />
            <p className="text-xs text-slate-400">Loading online orders...</p>
          </div>
        ) : pendingOrders.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 mx-auto flex items-center justify-center mb-3">
              <CheckCheck size={24} />
            </div>
            <h3 className="font-bold text-sm text-slate-800 dark:text-slate-200">
              No Pending Online Orders
            </h3>
            <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
              Sabhi online orders process ho chuke hain. Naya online order aate hi yahan live flash hoga.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSimulateTestOrder}
              disabled={simulatingOrder}
              className="mt-4 text-xs gap-1.5 border-dashed"
            >
              <PlusCircle size={14} />
              Simulate Test Online Order
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingOrders.map((order) => {
              const isApproving = approvingKotId === order.kot?.id;

              return (
                <div
                  key={order.id}
                  className="bg-white dark:bg-slate-900 border-2 border-amber-300 dark:border-amber-700/80 rounded-2xl p-5 shadow-md flex flex-col justify-between relative overflow-hidden transition-all hover:border-amber-400"
                >
                  {/* Top amber alert banner */}
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-400 to-orange-500"></div>

                  <div className="space-y-3.5">
                    {/* Header: Token / Order ID + Time Ago */}
                    <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2.5">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 rounded-lg bg-amber-100 dark:bg-amber-950/60 text-amber-900 dark:text-amber-200 text-xs font-black">
                          #{order.token_number || order.id.slice(0, 6).toUpperCase()}
                        </span>
                        <span className="text-[11px] font-semibold text-slate-400 flex items-center gap-1">
                          <Clock size={12} />
                          {formatTimeAgo(order.created_at)}
                        </span>
                      </div>
                      <span
                        className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md ${
                          order.payment_status === "paid"
                            ? "bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400"
                            : "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300"
                        }`}
                      >
                        {order.payment_status === "paid" ? "Paid" : "Pay on Delivery"}
                      </span>
                    </div>

                    {/* Customer Information */}
                    <div>
                      <h4 className="font-extrabold text-base text-slate-900 dark:text-slate-100">
                        {order.customer_name}
                      </h4>
                      <div className="mt-1 space-y-1">
                        <a
                          href={`tel:${order.customer_phone}`}
                          className="text-xs text-primary hover:underline flex items-center gap-1.5 font-medium"
                        >
                          <Phone size={12} />
                          <span>{order.customer_phone}</span>
                        </a>
                        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-start gap-1.5">
                          <MapPin size={12} className="shrink-0 mt-0.5 text-slate-400" />
                          <span className="line-clamp-2">{order.delivery_address}</span>
                        </p>
                      </div>
                    </div>

                    {/* Items List */}
                    <div className="bg-slate-50 dark:bg-slate-950/60 rounded-xl p-3 border border-slate-100 dark:border-slate-800/80 space-y-1.5">
                      <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400">
                        Ordered Items
                      </p>
                      <div className="space-y-1 max-h-36 overflow-y-auto">
                        {order.items.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-xs text-slate-700 dark:text-slate-300"
                          >
                            <span className="font-medium">
                              {item.name}{" "}
                              <span className="font-bold text-primary">×{item.quantity}</span>
                            </span>
                            {item.price ? (
                              <span className="text-slate-500 dark:text-slate-400">
                                ₹{item.price * item.quantity}
                              </span>
                            ) : null}
                          </div>
                        ))}
                      </div>

                      <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between font-black text-sm text-slate-900 dark:text-slate-100">
                        <span>Total Amount</span>
                        <span className="text-primary text-base">₹{order.total}</span>
                      </div>
                    </div>
                  </div>

                    {/* Action Buttons: Big Clear Approve & Reject */}
                  <div className="pt-4 mt-2 border-t border-slate-100 dark:border-slate-800 flex gap-2.5">
                    <Button
                      className="flex-1 h-11 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs gap-1.5"
                      onClick={() => handleApprove(order)}
                      disabled={isApproving}
                    >
                      {isApproving ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <CheckCircle2 size={18} />
                      )}
                      <span>Approve</span>
                    </Button>

                    <Button
                      variant="outline"
                      className="flex-1 h-11 text-sm font-bold border-red-200 dark:border-red-900/60 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 gap-1.5"
                      onClick={() => {
                        setRejectingOrder(order);
                        setRejectReason("");
                      }}
                      disabled={isApproving}
                    >
                      <XCircle size={18} />
                      <span>Reject</span>
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 3. DOOSRE SECTIONS (Neeche, Chhote / Collapsible) */}
      <div className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-800">
        {/* 3.1 IN KITCHEN SECTION */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
          <button
            onClick={() => setInKitchenOpen((v) => !v)}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">
                <ChefHat size={18} />
              </div>
              <div>
                <h3 className="font-bold text-base text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  In Kitchen (Approved)
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                    {inKitchenOrders.length}
                  </span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Orders currently being prepared in kitchen. KDS will mark them Ready when cooked.
                </p>
              </div>
            </div>

            {inKitchenOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

          {inKitchenOpen && (
            <div className="p-4 pt-0 border-t border-slate-100 dark:border-slate-800/60">
              {inKitchenOrders.length === 0 ? (
                <p className="text-xs text-slate-400 py-4 text-center">
                  Koi bhi online order abhi kitchen mein cooking stage par nahi hai.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-3">
                  {inKitchenOrders.map((order) => (
                    <div
                      key={order.id}
                      className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-950/40 space-y-2 text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                          #{order.token_number || order.id.slice(0, 6).toUpperCase()} -{" "}
                          {order.customer_name}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-400">
                          {order.kot?.status === "preparing" ? "Cooking Now" : "Queued"}
                        </span>
                      </div>

                      <div className="text-slate-600 dark:text-slate-400 space-y-0.5">
                        {order.items.map((i, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span>
                              {i.name} ×{i.quantity}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-800 text-[11px] text-slate-500">
                        <span>{formatTimeAgo(order.created_at)}</span>
                        <span className="font-bold text-slate-800 dark:text-slate-200">
                          ₹{order.total} ({order.payment_status})
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 3.2 READY FOR DELIVERY SECTION */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
          <button
            onClick={() => setReadyOpen((v) => !v)}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
                <PackageCheck size={18} />
              </div>
              <div>
                <h3 className="font-bold text-base text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  Ready for Delivery
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">
                    {readyOrders.length}
                  </span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Cooked & packed orders. Click &apos;Send for Delivery&apos; to assign bike/car driver.
                </p>
              </div>
            </div>

            {readyOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

          {readyOpen && (
            <div className="p-4 pt-0 border-t border-slate-100 dark:border-slate-800/60">
              {readyOrders.length === 0 ? (
                <p className="text-xs text-slate-400 py-4 text-center">
                  Abhi koi bhi order ready stage par nahi hai.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-3">
                  {readyOrders.map((order) => (
                    <div
                      key={order.id}
                      className="p-4 rounded-xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/30 dark:bg-emerald-950/20 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-extrabold text-sm text-slate-900 dark:text-slate-100">
                            #{order.token_number || order.id.slice(0, 6).toUpperCase()}
                          </span>
                          <h5 className="font-bold text-xs text-slate-800 dark:text-slate-200">
                            {order.customer_name}
                          </h5>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">
                          Ready Packed
                        </span>
                      </div>

                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        <p className="truncate">{order.delivery_address}</p>
                        <p className="mt-1 font-semibold text-slate-700 dark:text-slate-300">
                          ₹{order.total} • {order.items.length} items
                        </p>
                      </div>

                      <Button
                        className="w-full h-9 text-xs font-bold bg-primary hover:bg-primary/90 text-white gap-1.5"
                        onClick={() => {
                          setDispatchOrder(order);
                          setVehicleType("bike");
                        }}
                      >
                        <Bike size={15} />
                        <span>Send for Delivery</span>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 3.3 COMPLETED / REJECTED (COLLAPSED HISTORY) */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
          <button
            onClick={() => setHistoryOpen((v) => !v)}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-500/10 text-slate-600 dark:text-slate-400 flex items-center justify-center font-bold">
                <CheckCheck size={18} />
              </div>
              <div>
                <h3 className="font-bold text-base text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  Completed & Rejected History
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                    {historyOrders.length}
                  </span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Delivered/Served orders and rejected order history.
                </p>
              </div>
            </div>

            {historyOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

          {historyOpen && (
            <div className="p-4 pt-0 border-t border-slate-100 dark:border-slate-800/60">
              {historyOrders.length === 0 ? (
                <p className="text-xs text-slate-400 py-4 text-center">
                  Koi past completed ya rejected orders nahi hain.
                </p>
              ) : (
                <div className="space-y-2 pt-3">
                  {historyOrders.map((order) => {
                    const isRejected = order.kot?.status === "rejected";

                    return (
                      <div
                        key={order.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/30 gap-2"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                              isRejected
                                ? "bg-red-100 dark:bg-red-950 text-red-600"
                                : "bg-emerald-100 dark:bg-emerald-950 text-emerald-600"
                            }`}
                          >
                            {isRejected ? <XCircle size={15} /> : <CheckCircle2 size={15} />}
                          </div>
                          <div>
                            <p className="text-xs font-bold text-slate-900 dark:text-slate-100">
                              #{order.token_number || order.id.slice(0, 6).toUpperCase()} •{" "}
                              {order.customer_name}
                            </p>
                            <p className="text-[11px] text-slate-400">
                              {order.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}
                            </p>
                            {isRejected && (order.cancellation_reason || order.kot?.notes) && (
                              <p className="text-[11px] text-red-600 dark:text-red-400 font-medium">
                                Reason: {order.cancellation_reason || order.kot?.notes}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3 self-end sm:self-center">
                          <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                            ₹{order.total}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${
                              isRejected
                                ? "bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400"
                                : "bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400"
                            }`}
                          >
                            {isRejected ? "Rejected" : "Delivered"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* REJECT MODAL DIALOG */}
      {rejectingOrder && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full border border-slate-200 dark:border-slate-800 p-6 shadow-2xl space-y-4">
            <div>
              <h3 className="text-lg font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <XCircle size={20} className="text-red-600" />
                Reject Online Order
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Order #{rejectingOrder.token_number || rejectingOrder.id.slice(0, 6).toUpperCase()} •{" "}
                {rejectingOrder.customer_name} (₹{rejectingOrder.total})
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                Select or type reason for rejection:
              </label>

              {/* Quick Reason Chips */}
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[
                  "Kitchen busy / High order volume",
                  "Item out of stock",
                  "Address outside delivery area",
                  "Restaurant closed",
                  "Customer cancellation request",
                ].map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => setRejectReason(reason)}
                    className={`text-[11px] px-2.5 py-1 rounded-lg border transition-colors ${
                      rejectReason === reason
                        ? "bg-red-500 text-white border-red-500"
                        : "bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    {reason}
                  </button>
                ))}
              </div>

              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason for customer..."
                rows={3}
                className="w-full text-xs p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500/20"
              />
            </div>

            <div className="flex gap-2.5 pt-2">
              <Button
                variant="outline"
                className="flex-1 h-10 text-xs"
                onClick={() => setRejectingOrder(null)}
                disabled={submittingReject}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-10 text-xs font-bold bg-red-600 hover:bg-red-700 text-white"
                onClick={handleConfirmReject}
                disabled={submittingReject}
              >
                {submittingReject ? (
                  <Loader2 size={15} className="animate-spin mr-1" />
                ) : (
                  <XCircle size={15} className="mr-1" />
                )}
                Confirm Reject
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* DISPATCH / SEND FOR DELIVERY VEHICLE MODAL */}
      {dispatchOrder && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full border border-slate-200 dark:border-slate-800 p-6 shadow-2xl space-y-4">
            <div>
              <h3 className="text-lg font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Bike size={20} className="text-primary" />
                Send for Delivery
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Assign vehicle type for #{dispatchOrder.token_number || dispatchOrder.id.slice(0, 6).toUpperCase()} •{" "}
                {dispatchOrder.customer_name}
              </p>
            </div>

            <div className="p-3 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-100 dark:border-slate-800 text-xs space-y-1">
              <p className="font-bold text-slate-800 dark:text-slate-200">
                Delivery Address:
              </p>
              <p className="text-slate-600 dark:text-slate-400">
                {dispatchOrder.delivery_address}
              </p>
              <p className="text-slate-500 pt-1 border-t border-slate-200 dark:border-slate-800">
                Total: ₹{dispatchOrder.total} ({dispatchOrder.payment_status})
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                Select Vehicle Type:
              </label>
              <div className="grid grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => setVehicleType("bike")}
                  className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                    vehicleType === "bike"
                      ? "border-primary bg-primary/10 text-primary font-bold shadow-xs"
                      : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-50"
                  }`}
                >
                  <Bike size={24} />
                  <div className="text-center">
                    <p className="text-xs font-bold">Bike / 2-Wheeler</p>
                    <p className="text-[10px] opacity-75">Quick City Delivery</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setVehicleType("car")}
                  className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                    vehicleType === "car"
                      ? "border-primary bg-primary/10 text-primary font-bold shadow-xs"
                      : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-50"
                  }`}
                >
                  <Car size={24} />
                  <div className="text-center">
                    <p className="text-xs font-bold">Car / 4-Wheeler</p>
                    <p className="text-[10px] opacity-75">Bulk / Large Orders</p>
                  </div>
                </button>
              </div>
            </div>

            <div className="flex gap-2.5 pt-2">
              <Button
                variant="outline"
                className="flex-1 h-10 text-xs"
                onClick={() => setDispatchOrder(null)}
                disabled={dispatching}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-10 text-xs font-bold bg-primary hover:bg-primary/90 text-white"
                onClick={handleConfirmDispatch}
                disabled={dispatching}
              >
                {dispatching ? (
                  <Loader2 size={15} className="animate-spin mr-1" />
                ) : (
                  <Bike size={15} className="mr-1" />
                )}
                Confirm & Dispatch
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
